import { FC, useRef, useMemo, useState, useEffect } from 'react'
import {
  Paper,
  Box,
  CircularProgress,
  IconButton,
  TableContainer,
  Table,
  styled,
  TableBody,
} from '@mui/material'
import ViewIcon from '@/assets/icons/actions/view.svg'
import { useVirtualizedTable } from '@/hooks/shared/useVirtualizedTable'
import {
  TableWrapper,
  TableContainerStyled,
  VirtualTableContainer,
  VirtualRow,
  VirtualCell,
} from '@/components/table/StyledTableComponents'
import { TableHeader } from '@/components/table/TableHeader'
import { NoResultsAlert } from '@/components/notification/NoResultsAlert'
import type { SortableTableProps } from '@/utilities/types/table-props'
import type { TableColumn, ProvinceData } from '@/utilities/types/table'
import DateFormatter from '@/utilities/date-formatter'
import ViewProvinceModal from '@/components/modal/area-management/provinces/ViewProvinceModal'
import { AreaControllerTableFooter } from '../../AreaControllerTableFooter'
import { useProvincesVisualizedTable } from '@/hooks/shared/useProvincesVisualizedTable'
import { ProvincesTableFooter } from '../../ProvincesTableFooter'
import dayjs from 'dayjs'

// Define a more specific interface name to avoid conflict with built-in FormData
interface ProvinceFormData {
  province: string
  area: string
}

export const StyledRow = styled(Box)(() => ({
  display: 'flex',
  width: '100%',
  height: '52px',
  padding: '16px',
  '&:nth-of-type(even)': {
    backgroundColor: '#f5f5f5',
  },
  '&:nth-of-type(odd)': {
    backgroundColor: 'white',
  },
}))

export const StyledCell = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  wordBreak: 'break-word',
  textAlign: 'center',
  fontSize: '12px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '10px',
  },
}))

const columns: TableColumn[] = [
  { id: 'sourceProvinceId', label: 'ID', width: '10%', sortable: true },
  { id: 'name', label: 'Name', width: '25%', sortable: true },
  { id: 'regionName', label: 'Region', width: '25%', sortable: true },
  { id: 'dateCreated', label: 'Created Date', width: '15%', sortable: true },
  { id: 'dateModified', label: 'Modified Date', width: '15%', sortable: true },
  { id: 'action', label: 'Action', width: '10%', align: 'center' },
]

interface ProvinceTableProps extends SortableTableProps<ProvinceData> {}

export const AreaManagementProvinceTable: FC<ProvinceTableProps> = ({
  data = [],
  filterState,
  isLoading,
  error,
  onSort,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  totalElem,
  handleSort,
}) => {
  const parentRef = useRef<HTMLDivElement>(null)
  const [showNoResults, setShowNoResults] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedProvince, setSelectedProvince] = useState<ProvinceData | null>(
    null,
  )

  const {
    virtualRows,
    totalSize,
    paginatedItems,
    handlePageChange,
    handleRowsPerPageChange,
    handleVirtualizedScroll,
  } = useProvincesVisualizedTable({
    data,
    filterState,
    containerRef: parentRef,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    totalElem,
  })

  useEffect(() => {
    if (data.length <= 0 && isLoading) {
      setShowNoResults(true)
    }
  }, [data.length])

  const handleModalOpen = (province: ProvinceData) => {
    setSelectedProvince(province)
    setModalOpen(true)
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          height: '70vh',
          width: '100%',
          background: 'white',
          zIndex: 1,
          display: 'fleex',
          justifyContent: 'center',
          alignItems: 'center',
          borderBottomLeftRadius: '10px',
          borderBottomRightRadius: '10px',
          marginTop: '2px',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', color: 'error.main' }}>
        Error: {error instanceof Error ? error.message : 'Unknown error'}
      </Box>
    )
  }

  return (
    <div
      style={{
        boxShadow: 'none',
        width: '100%',
        zIndex: 1,
        marginTop: '2px',
      }}
    >
      <TableContainer component={Paper}>
        <Table
          sx={{ minWidth: 650, minHeight: '600px' }}
          aria-label="simple table"
        >
          <TableHeader
            columns={columns}
            filterState={filterState}
            onSort={onSort}
            handleSort={handleSort}
          />
          <TableBody
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}
          >
            {paginatedItems.map((row, index) => (
              <StyledRow key={index}>
                {columns.map((column) => {
                  if (column.id === 'sourceProvinceId') {
                    return (
                      <StyledCell style={{ width: column.width }}>
                        {row.sourceProvinceId}
                      </StyledCell>
                    )
                  }
                  if (column.id === 'name') {
                    return (
                      <StyledCell width={column.width}>{row.name}</StyledCell>
                    )
                  }

                  if (column.id === 'regionName') {
                    return (
                      <StyledCell width={column.width}>
                        {row.regionName}
                      </StyledCell>
                    )
                  }
                  if (column.id === 'dateCreated') {
                    return (
                      <StyledCell width={column.width}>
                        {dayjs(row.dateCreated).format('MMM DD, YYYY')}
                      </StyledCell>
                    )
                  }
                  if (column.id === 'dateModified') {
                    return (
                      <StyledCell width={column.width}>
                        {dayjs(row.dateModified).format('MMM DD, YYYY')}
                      </StyledCell>
                    )
                  }
                  if (column.id === 'action') {
                    return (
                      <StyledCell width={column.width}>
                        <IconButton
                          onClick={() => handleModalOpen(row)}
                          sx={{
                            padding: '4px',
                            '&:hover': { bgcolor: 'rgba(73, 90, 255, 0.05)' },
                          }}
                        >
                          <img
                            src={ViewIcon}
                            alt="View"
                            width={16}
                            height={16}
                          />
                        </IconButton>
                      </StyledCell>
                    )
                  }
                })}
              </StyledRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <ViewProvinceModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false)
          setSelectedProvince(null)
        }}
        data={selectedProvince}
      />
      <ProvincesTableFooter
        rowsPerPage={rowsPerPage}
        totalCount={totalElem}
        page={page}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </div>
  )
}
